var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
import React from 'react';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, cPointAtOrigin } from '@shapeable/utils';
var cls = classNames('arc-button');
import { lighten } from 'polished';
import { animated, useSpring, to, easings } from '@react-spring/web';
import { TextArc } from './text-arc';
export var ArcButtonDefaultProps = {
  outerRadius: 492,
  innerRadius: 462,
  color: 'black',
  duration: 1000,
  tabAngleDelta: 3.7,
  highlightOnHover: true
};
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    ", "\n  "], ["\n    ", "\n  "])), function (_a) {
    var highlightOnHover = _a.highlightOnHover,
      _color = _a._color,
      isInteractive = _a.isInteractive;
    return isInteractive && highlightOnHover && css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      &:hover {\n        ", " {\n          fill: ", ";\n        }\n      }\n    "], ["\n      &:hover {\n        ", " {\n          fill: ", ";\n        }\n      }\n    "])), cls.dot('path'), lighten(0.05, _color));
  })
});
var ShapeStyles = breakpoints({
  base: css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    transition: fill 0.2s;\n    ", "\n  "], ["\n    transition: fill 0.2s;\n    ", "\n  "])), function (_a) {
    var _color = _a._color,
      isInteractive = _a.isInteractive;
    return css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      fill: ", ";\n      cursor: ", ";\n    "], ["\n      fill: ", ";\n      cursor: ", ";\n    "])), _color, isInteractive ? 'pointer' : 'default');
  })
});
var LabelStyles = breakpoints({
  base: css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    user-select: none;\n  "], ["\n    user-select: none;\n  "])))
});
var ChevronStyles = breakpoints({
  base: css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    stroke: ", ";\n  "], ["\n    stroke: ", ";\n  "])), theme.COLOR('light'))
});
// -------- Components -------->
var My = {
  Container: styled(animated.svg)(templateObject_7 || (templateObject_7 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Path: styled(animated.path).attrs(cls.attr('path'))(templateObject_8 || (templateObject_8 = __makeTemplateObject(["", ""], ["", ""])), ShapeStyles),
  Label: styled(TextArc).attrs(cls.attr('label'))(templateObject_9 || (templateObject_9 = __makeTemplateObject(["", ""], ["", ""])), LabelStyles),
  Chevron: styled(animated.path)(templateObject_10 || (templateObject_10 = __makeTemplateObject(["", ""], ["", ""])), ChevronStyles)
};
export var ArcButton = function (props) {
  var className = props.className,
    outerRadius = props.outerRadius,
    innerRadius = props.innerRadius,
    id = props.id,
    color = props.color,
    style = props.style,
    onClick = props.onClick,
    originX = props.originX,
    originY = props.originY,
    headAngle = props.headAngle,
    tailAngle = props.tailAngle,
    startHeadAngle = props.startHeadAngle,
    startTailAngle = props.startTailAngle,
    duration = props.duration,
    children = props.children,
    label = props.label,
    highlightOnHover = props.highlightOnHover,
    tabAngleDelta = props.tabAngleDelta;
  var size = outerRadius * 2;
  var cPointAt = cPointAtOrigin(originX, originY);
  var hasStartingValues = startHeadAngle !== undefined && startTailAngle !== undefined;
  var isAnimated = hasStartingValues && !!duration;
  var tabRadius = outerRadius - innerRadius;
  var values = !isAnimated ? {
    headAngle: headAngle,
    tailAngle: tailAngle
  } : useSpring({
    from: {
      headAngle: startHeadAngle,
      tailAngle: startTailAngle
    },
    to: {
      headAngle: headAngle,
      tailAngle: tailAngle
    },
    // config: { tension: 130, friction: 13, bounce: 0 },
    config: {
      duration: duration,
      easing: easings.easeOutCirc
    },
    onRest: props.onRest
  });
  var tabAngle = tailAngle + (headAngle - tailAngle) / 2;
  var labelRadius = innerRadius + (outerRadius - innerRadius) / 2;
  var normalizedTabAngle = tabAngle % 360;
  var isLower = normalizedTabAngle > 0 && normalizedTabAngle < 180;
  var startAngle = isLower ? headAngle : tailAngle;
  var endAngle = isLower ? tailAngle : headAngle;
  var ascending = !isLower;
  var labelStart = cPointAt(labelRadius, startAngle);
  var labelEnd = cPointAt(labelRadius, endAngle);
  var shapeDef = function (headAngle, tailAngle) {
    var tabAngle = tailAngle + (headAngle - tailAngle) / 2;
    var tailInner = cPointAt(innerRadius, tailAngle);
    var tailOuter = cPointAt(outerRadius, tailAngle);
    var tabOrigin = cPointAt(outerRadius, tabAngle);
    var cPointTab = cPointAtOrigin(tabOrigin.cx, tabOrigin.cy);
    // not sure how to calculate the tab angle delta yet... this was trial-and-error (eek).
    var tabStart = cPointAt(outerRadius, tabAngle - 3.7);
    var tabEnd = cPointAt(outerRadius, tabAngle + 3.7);
    var headInner = cPointAt(innerRadius, headAngle);
    var headOuter = cPointAt(outerRadius, headAngle);
    // the large arc flag needs to change depending on the angle difference between the tail and head
    // else it produces incorrect arcs for angles > 180 degrees
    var difference = headAngle - tailAngle;
    return "\n      M".concat(tailInner.cx, ",").concat(tailInner.cy, " \n      L").concat(tailOuter.cx, ",").concat(tailOuter.cy, " \n\n      A").concat(outerRadius, " ").concat(outerRadius, " 0 0 1 ").concat(tabStart.cx, " ").concat(tabStart.cy, "\n      A").concat(tabRadius, " ").concat(tabRadius, " 0 0 1 ").concat(tabEnd.cx, " ").concat(tabEnd.cy, "\n\n      A").concat(outerRadius, " ").concat(outerRadius, " 0 0 1 ").concat(headOuter.cx, " ").concat(headOuter.cy, "\n      L").concat(headInner.cx, ",").concat(headInner.cy, " \n      A").concat(innerRadius, " ").concat(innerRadius, " 0 0 0 ").concat(tailInner.cx, " ").concat(tailInner.cy, "\n    ");
  };
  var chevronStart = cPointAt(outerRadius + tabRadius * 3 / 10, tabAngle - 0.9);
  var chevronMiddle = cPointAt(outerRadius + tabRadius * 5.5 / 10, tabAngle);
  var chevronEnd = cPointAt(outerRadius + tabRadius * 3 / 10, tabAngle + 0.9);
  return React.createElement(My.Container, {
    highlightOnHover: highlightOnHover,
    _color: color,
    isInteractive: !!onClick,
    overflow: "visible",
    width: size,
    height: size,
    id: "slice-".concat(id),
    style: style,
    className: cls.name(className),
    viewBox: "0 0 ".concat(size, " ").concat(size)
  }, React.createElement(My.Path, {
    fill: color,
    _color: color,
    isInteractive: !!onClick,
    d: to([values.headAngle, values.tailAngle], shapeDef),
    onClick: onClick,
    className: "shape"
  }), React.createElement(My.Chevron, {
    fill: "none",
    stroke: "#FFFFFF",
    strokeWidth: 2.5,
    d: "M".concat(chevronStart.cx, " ").concat(chevronStart.cy, " L").concat(chevronMiddle.cx, " ").concat(chevronMiddle.cy, " L").concat(chevronEnd.cx, " ").concat(chevronEnd.cy)
  }), React.createElement(My.Label, {
    onClick: onClick,
    fontWeight: 400,
    id: "".concat(id, "-label"),
    x1: labelStart.cx,
    y1: labelStart.cy,
    x2: labelEnd.cx,
    y2: labelEnd.cy,
    ascending: ascending,
    r: labelRadius,
    alignmentBaseline: 'central',
    color: 'light'
  }, label || children));
};
ArcButton.cls = cls;
ArcButton.defaultProps = ArcButtonDefaultProps;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10;